import { html } from '@polymer/polymer/polymer-element';
import { KatapultWidget } from './katapult-widget';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';

import '../../paper-table/paper-table';
import '../../style-modules/paper-table-style';
import '../../paper-header/paper-header';

import '@polymer/paper-menu-button/paper-menu-button.js';
import '../../style-modules/paper-menu-button-style.js';
import '@polymer/iron-icons/av-icons';
import '../../katapult-elements/katapult-dialog.js';

class TrainingWidget extends KatapultWidget {
  static get widgetTitleTemplate() {
    return html`Training Widget`;
  }

  static get bodyTemplate() {
    return html`
      <style include="paper-table-style">
        /* General Styling */
        :host {
          --accent-color: #ff8300;
          --text-color: rgba(0, 0, 0, 0.9);
        }
        paper-row {
          height: auto;
        }
        .subtleText {
          color: grey;
        }
        .subtleTextHover:hover {
          color: var(--text-color);
        }
        .clickable {
          cursor: pointer;
        }
        .fit-content {
          max-width: fit-content;
        }
        .font14 {
          font-size: 14px;
        }
        .font16 {
          font-size: 16px;
        }
        .font18 {
          font-size: 18px;
        }
        .removeDefaultInputStyle {
          border: none;
          background: none;
          outline: none;
          padding: 0;
        }
        .removeLinkStyling {
          color: var(--text-color);
          text-decoration: none;
        }
        .itemWrapper paper-cell,
        .attributeGroupHeader paper-cell,
        .adminItemWrapper paper-cell {
          padding: 0;
        }
        /* Row/Group Styling */
        .attributeGroupHeader {
          background-color: var(--paper-grey-100);
          width: 100%;
          font-size: 11pt;
          transition: all 0.3s;
          padding: 8px 16px;
          z-index: 1;
          box-sizing: border-box;
        }
        .attributeGroupHeader[raised] {
          border-bottom: 1px solid var(--paper-grey-200);
        }
        .attributeGroupHeader > iron-icon[rotate=''],
        katapult-icon[rotate=''] {
          transform: rotate(-180deg);
        }
        .moduleAttributes {
          height: 10px;
          line-height: 10px;
          width: fit-content;
          padding: 4px 8px;
          margin: 0 6px;
          border-radius: 16px;
        }
        .itemWrapper {
          box-sizing: border-box;
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          border-top: 1px solid var(--paper-grey-200);
        }
        .itemWrapper:hover {
          background: #fafafa;
        }
        .adminItemWrapper {
          box-sizing: border-box;
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          border-top: 1px solid var(--paper-grey-200);
          padding: 10px 16px;
        }
        .sectionTitle {
          font-size: 20px;
          font-weight: 500;
          padding: 0 8px 0 8px;
        }
        #blankModule {
          width: 824px;
          font-size: 18px;
          background-color: var(--paper-grey-100);
          transition: all 0.3s;
          z-index: 1;
          box-sizing: border-box;
          padding: 8px 16px;
          padding-left: 14px;
        }
        katapult-icon[lit] {
          color: var(--accent-color);
        }
        .link-hover:hover {
          color: var(--paper-grey-800);
        }
        input[disabled] {
          pointer-events: none;
        }
        .block-tools-item {
          display: flex;
          align-items: center;
          padding: 8px;
          white-space: nowrap;
          color: var(--paper-grey-700);
        }
        .block-tools-item:hover {
          color: var(--text-color);
        }
      </style>

      <!-- Database Listeners -->
      <!-- Keeps active user progress and assigments updated -->
      <katapult-firebase-worker path="photoheight/home/training_widget/user_data/[[userGroup]]/[[user.uid]]" data="{{myWidgetData}}">
      </katapult-firebase-worker>
      <!-- Keeps the links and module data up to date -->
      <katapult-firebase-worker path="photoheight/home/training_widget/modules" data="{{moduleData}}"> </katapult-firebase-worker>
      <!-- Reads the company directory within company spaces -->
      <katapult-firebase-worker path="photoheight/company_space/[[userGroup]]" data="{{companySpaceData}}"> </katapult-firebase-worker>
      <!-- Looks for new users in the company widget data -->
      <katapult-firebase-worker
        path="photoheight/home/training_widget/user_data/[[userGroup]]"
        data="{{widgetUsers}}"
        disabled="[[!companyAdmin]]"
      >
      </katapult-firebase-worker>
      <!-- Grabs all user data (if modules or items are deleted, we can delete the module/item from under each user as well) -->
      <katapult-firebase-worker path="photoheight/home/training_widget/user_data" data="{{allUserData}}" disabled="[[!moduleAdmin]]">
      </katapult-firebase-worker>

      <!-- Header -->
      <paper-row>
        <template is="dom-if" if="[[amAdmin(moduleAdmin, companyAdmin)]]">
          <paper-cell class="clickable fit-content font16" style="color: var(--text-color); padding: 0; padding-left: 6px;">
            <!-- Dropdown To Select Admin Mode -->
            <paper-menu-button id="settingDropdown" style="padding: 0; display: block;" vertical-offset="20">
              <material-icon size="small" icon="settings" slot="dropdown-trigger"></material-icon>
              <div slot="dropdown-content">
                <template is="dom-if" if="[[checkAdminMode(moduleAdmin, moduleAdminMode)]]">
                  <div class="block-tools-item" on-click="enableModuleAdminView">
                    <katapult-icon style="padding-right: 4px;" icon="assignment" size="20"></katapult-icon>
                    Edit Modules
                  </div>
                </template>
                <template is="dom-if" if="[[checkAdminMode(companyAdmin, companyAdminMode)]]">
                  <div class="block-tools-item" on-click="enableCompanyAdminView">
                    <katapult-icon style="padding-right: 4px;" icon="assignment_ind" size="20"></katapult-icon>
                    Edit Users
                  </div>
                </template>
                <template is="dom-if" if="[[amAdmin(companyAdminMode, moduleAdminMode)]]">
                  <div class="block-tools-item" on-click="disableAdminView">
                    <katapult-icon style="padding-right: 4px;" icon="exit_to_app" size="20"></katapult-icon>
                    Exit Admin Mode
                  </div>
                </template>
              </div>
            </paper-menu-button>
          </paper-cell>
        </template>
        <!-- Title Row -->
        <template is="dom-if" if="[[!companyAdminMode]]">
          <paper-cell class="sectionTitle fit-content">Training Modules</paper-cell>
        </template>
        <template is="dom-if" if="[[companyAdminMode]]">
          <paper-cell class="sectionTitle fit-content">Company Users</paper-cell>
        </template>
        <template is="dom-if" if="[[companyAdminMode]]">
          <paper-cell class="clickable fit-content font16 subtleText subtleTextHover" style="padding: 8px;" on-click="saveUserChanges">
            <span>Save Changes</span>
            <katapult-icon style="margin-left: 4px;" icon="save" size="22"></katapult-icon>
          </paper-cell>
        </template>
        <template is="dom-if" if="[[companyAdminMode]]">
          <paper-cell class="clickable fit-content font16 subtleText subtleTextHover" style="padding: 8px;" on-click="exportUserData">
            <span>Export User Data</span>
            <katapult-icon style="margin: 0 4px 0 4px;" icon="file_download" size="22"></katapult-icon>
          </paper-cell>
        </template>
        <paper-cell></paper-cell>
        <template is="dom-if" if="[[moduleAdminMode]]">
          <paper-cell class="clickable fit-content font16 subtleText subtleTextHover" style="padding: 8px;" on-click="toggleEditing">
            <span>Toggle Editing</span>
            <katapult-icon style="margin: 0 2px 0 4px;" icon="build" size="22"></katapult-icon>
          </paper-cell>
        </template>
        <!-- Collapse and Expand All Button -->
        <paper-cell class="subtleText subtleTextHover clickable fit-content font16" style="padding: 8px;" on-click="toggleModules">
          <template is="dom-if" if="[[!rowToggleFlag]]">
            <div style="display: flex; flex-direction: row;">
              <span style="margin: auto;">Expand All</span>
              <katapult-icon icon="unfold_more"></katapult-icon>
            </div>
          </template>
          <template is="dom-if" if="[[rowToggleFlag]]">
            <div style="display: flex; flex-direction: row;">
              <span style="margin: auto;">Collapse All</span>
              <katapult-icon icon="unfold_less"></katapult-icon>
            </div>
          </template>
        </paper-cell>
      </paper-row>

      <!-- Katapult Admin View -->
      <template is="dom-if" if="[[moduleAdminMode]]">
        <template is="dom-repeat" items="{{trainingData}}" as="module" index-as="moduleIndex">
          <paper-row id="Title [[moduleIndex]]" class="attributeGroupHeader" raised$="[[module.adminopened]]" on-click="toggleGroupRow">
            <paper-cell class="fit-content font18">
              <template is="dom-if" if="[[!ableToEdit]]">
                <input
                  type="text"
                  id="Width [[moduleIndex]]"
                  class="font18 removeDefaultInputStyle"
                  style="color: black;"
                  value="[[module.moduletitle]]"
                  disabled
                />
              </template>
              <template is="dom-if" if="[[ableToEdit]]">
                <input
                  type="text"
                  id="[[moduleIndex]],Title"
                  class="font18 removeDefaultInputStyle"
                  style="width: 100%; max-width: 700px;"
                  value="[[module.moduletitle]]"
                  on-change="updateData"
                />
              </template>
            </paper-cell>
            <paper-cell></paper-cell>
            <!-- Module Icons (Dropdown, Delete) -->
            <paper-cell class="fit-content">
              <iron-collapse opened="[[ableToEdit]]">
                <katapult-icon id="[[moduleIndex]] " size="22" icon="delete" on-click="deleteModule"></katapult-icon>
              </iron-collapse>
            </paper-cell>
            <paper-cell id="Arrow [[moduleIndex]]" class="fit-content" on-click="toggleGroupRow">
              <katapult-icon icon="keyboard_arrow_down" rotate$="[[module.adminopened]]"></katapult-icon>
            </paper-cell>
          </paper-row>
          <!-- Module Content -->
          <iron-collapse opened="[[module.adminopened]]">
            <template is="dom-repeat" items="[[module.items]]" index-as="itemIndex">
              <div class="adminItemWrapper">
                <paper-row>
                  <!-- Leftside Data for Item Row (Title, Description, Link, Completion Date) -->
                  <paper-cell>
                    <div style="width: 100%; height 100%;">
                      <template is="dom-if" if="[[!ableToEdit]]">
                        <input
                          type="text"
                          class="font18 removeDefaultInputStyle"
                          style="color: black; width: 100%; max-width: 700px;"
                          value="[[item.title]]"
                          disabled
                        />
                        <template is="dom-if" if="[[item.description]]">
                          <input
                            type="text"
                            class="subtleText font14 removeDefaultInputStyle"
                            style="width: 100%; max-width: 700px;"
                            value="[[item.description]]"
                            disabled
                          />
                        </template>
                      </template>
                      <template is="dom-if" if="[[ableToEdit]]">
                        <input
                          type="text"
                          id="[[moduleIndex]],[[itemIndex]],Title"
                          class="font18 removeDefaultInputStyle"
                          style="width: 100%; max-width: 700px;"
                          value="[[item.title]]"
                          on-change="updateData"
                        />
                        <input
                          type="text"
                          id="[[moduleIndex]],[[itemIndex]],Description"
                          class="subtleText font14 removeDefaultInputStyle"
                          style="width: 100%; max-width: 700px;"
                          value="[[item.description]]"
                          on-change="updateData"
                        />
                      </template>
                      <paper-cell style="opacity: .8;">
                        <a href="[[item.link]]" target="_blank">
                          <katapult-icon class="link-hover" color="grey" size="18" icon="link"></katapult-icon>
                        </a>
                        <template is="dom-if" if="[[!ableToEdit]]">
                          <input
                            type="url"
                            class="subtleText font14 removeDefaultInputStyle"
                            style="padding-left: 4px; width: 100%; max-width: 700px;"
                            value="[[item.link]]"
                            disabled
                          />
                        </template>
                        <template is="dom-if" if="[[ableToEdit]]">
                          <input
                            type="url"
                            id="[[moduleIndex]],[[itemIndex]],Link"
                            class="subtleText font14 removeDefaultInputStyle"
                            style="padding-left: 4px; width: 100%; max-width: 700px;"
                            value="[[item.link]]"
                            on-change="updateData"
                          />
                        </template>
                      </paper-cell>
                    </div>
                  </paper-cell>
                  <!-- Item Completion/Required Buttons -->
                  <paper-cell class="fit-content">
                    <template is="dom-if" if="[[item.markcomplete]]">
                      <template is="dom-if" if="[[!ableToEdit]]">
                        <div class="subtleText">Required</div>
                      </template>
                    </template>
                    <template is="dom-if" if="[[ableToEdit]]">
                      <iron-collapse class="clickable" style="display: flex; flex-direction: row;" opened="[[ableToEdit]]">
                        <katapult-icon
                          id="[[moduleIndex]].[[itemIndex]]"
                          lit$="[[item.markcomplete]]"
                          weight="500"
                          icon="verified_user"
                          on-click="makeRequired"
                        ></katapult-icon>
                        <katapult-icon
                          id="[[moduleIndex]]-[[itemIndex]]"
                          size="22"
                          icon="close"
                          style="margin-left: 6px;"
                          on-click="deleteItem"
                        ></katapult-icon>
                      </iron-collapse>
                    </template>
                  </paper-cell>
                </paper-row>
              </div>
            </template>
            <!-- Blank "Add Item" Holder for Edit Modules Mode -->
            <iron-collapse opened="[[ableToEdit]]">
              <paper-row class="clickable" id="[[moduleIndex]]" style="border-top: 1px solid var(--paper-grey-200);" on-click="addItem">
                <paper-cell class="fit-content" style="padding-right: 4px; padding-left: 14px;">
                  <katapult-icon size="22" icon="add"></katapult-icon>
                </paper-cell>
                <paper-cell style="padding-left: 0;">
                  <span class="font16">Add Item</span>
                </paper-cell>
              </paper-row>
            </iron-collapse>
          </iron-collapse>
          <template> </template
        ></template>
        <!-- Blank "Add Module" Holder for Edit Modules Mode -->
        <iron-collapse opened="[[ableToEdit]]">
          <paper-row class="clickable" id="blankModule" on-click="addModule">
            <paper-cell class="fit-content" style="padding: 0;">
              <katapult-icon icon="add"></katapult-icon>
            </paper-cell>
            <paper-cell style="padding: 0;">
              <input type="text" class="font18 removeDefaultInputStyle" style="width: 100px;" value="Add Module" disabled />
            </paper-cell>
          </paper-row>
        </iron-collapse>
      </template>

      <!-- Company Admin View -->
      <template is="dom-if" if="[[companyAdminMode]]">
        <paper-row>
          <paper-cell class="fit-content" style="padding: 0; height: 46px;">
            <katapult-button style="padding: 4px; margin: 0 4px 4px 4px;" class="font14" size="small" on-click="addUser"
              >Submit</katapult-button
            >
          </paper-cell>
          <paper-cell style="padding: 0 4px 0 4px; height: 46px;">
            <katapult-drop-down
              id="user-selector"
              label="Select a user to add"
              no-label-float
              sort-alphabetically
              items="{{availableUserEmails}}"
            >
            </katapult-drop-down>
          </paper-cell>
        </paper-row>
        <template is="dom-repeat" items="{{companyUsers}}" index-as="userIndex">
          <paper-row class="attributeGroupHeader clickable" id="[[item.email]]" raised$="[[item.opened]]" on-click="toggleUserRow">
            <paper-cell id="[[item.email]] delete" class="fit-content" style="margin-right: 12px;" on-click="deleteUser">
              <katapult-icon id="[[userIndex]] Close" icon="close" size="22"></katapult-icon>
            </paper-cell>
            <paper-cell>
              <span class="font18" style="padding-right: 4px;">[[item.email]]</span>
            </paper-cell>
            <paper-cell>
              <katapult-icon style="margin-left: auto;" icon="expand_more" size="22"></katapult-icon>
            </paper-cell>
          </paper-row>

          <iron-collapse opened="[[item.opened]]" style="padding: 12px 50px;">
            <paper-row class="font16" style="padding-bottom: 4px;">
              <paper-cell style="padding: 0;">Assign Modules</paper-cell>
            </paper-row>
            <template is="dom-repeat" items="[[item.modules]]" as="module">
              <paper-row>
                <paper-cell style="padding: 4px 0 4px 0;">
                  <paper-checkbox
                    id="[[item.email]]"
                    style="--paper-checkbox-size: 14px;"
                    checked="[[module.assigned]]"
                    on-click="updateAssignment"
                    >[[module.title]]</paper-checkbox
                  >
                </paper-cell>
              </paper-row>
            </template>
          </iron-collapse>
        </template>
      </template>

      <!-- User View -->
      <template is="dom-if" if="[[!amAdmin(companyAdminMode, moduleAdminMode)]]">
        <template is="dom-repeat" items="{{trainingData}}" as="module" index-as="moduleIndex">
          <paper-row
            id="Title [[moduleIndex]]"
            class="attributeGroupHeader clickable"
            raised$="[[module.useropened]]"
            on-click="toggleGroupRow"
          >
            <paper-cell class="fit-content" style="display: flex; flex-direction: column;">
              <div style="display: flex; flex-direction: row; margin: auto auto auto 0;">
                <div class="font18">[[module.moduletitle]]</div>
                <template is="dom-if" if="[[module.action]]">
                  <div class="moduleAttributes" style="background: var(--accent-color); color: #f2f2f2; margin: auto 0 auto 6px;">
                    Action Needed
                  </div>
                </template>
                <template is="dom-if" if="[[module.progress]]">
                  <div class="moduleAttributes" style="background: #007299; color: #f2f2f2; margin: auto 0 auto 6px;">In Progress</div>
                </template>
              </div>
              <template is="dom-if" if="[[module.dateCompleted]]">
                <div class="subtleText font16" style="margin: auto auto auto 0;">Completed: [[module.dateCompleted]]</div>
              </template>
            </paper-cell>
            <paper-cell></paper-cell>
            <!-- Module Icons (Dropdown, Delete) -->
            <paper-cell class="fit-content">
              <katapult-icon icon="keyboard_arrow_down" rotate$="[[module.useropened]]"></katapult-icon>
            </paper-cell>
          </paper-row>
          <!-- Module Content -->
          <iron-collapse opened="[[module.useropened]]">
            <template is="dom-repeat" items="[[module.items]]" index-as="itemIndex">
              <div class="itemWrapper">
                <paper-row>
                  <!-- Leftside Data for Item Row (Title, Description, Link, Completion Date) -->
                  <paper-cell>
                    <a
                      href="[[item.link]]"
                      target="_blank"
                      class="removeLinkStyling"
                      style="width: 100%; height 100%; min-height: 40px; padding: 10px 0 10px 16px; display: flex; align-items: center;"
                    >
                      <div style="width: 100%; height 100%;">
                        <div class="font18">[[item.title]]</div>
                        <template is="dom-if" if="[[item.description]]">
                          <div class="subtleText font14">[[item.description]]</div>
                        </template>
                        <template is="dom-if" if="[[item.markcomplete]]">
                          <template is="dom-if" if="[[item.date]]">
                            <div class="subtleText font14">Submitted: [[item.date]]</div>
                          </template>
                        </template>
                      </div>
                    </a>
                  </paper-cell>
                  <!-- Item Completion/Required Buttons -->
                  <template is="dom-if" if="[[item.markcomplete]]">
                    <template is="dom-if" if="[[item.assigned]]">
                      <template is="dom-if" if="[[!item.completed]]">
                        <paper-cell class="fit-content">
                          <div style="display: flex; flex-direction: column; min-height: 100%;">
                            <a href="[[item.link]]" target="_blank" class="removeLinkStyling" style="height: 10px; width: 100%;"></a>
                            <katapult-button
                              id="[[moduleIndex]]-[[itemIndex]]"
                              small
                              color="#ffffff"
                              style="width: 130px;"
                              textColor="var(--text-color)"
                              on-click="openDialog"
                              >Mark Complete</katapult-button
                            >
                            <a href="[[item.link]]" target="_blank" style="height: 10px; width: 100%;"></a>
                          </div>
                        </paper-cell>
                      </template>
                    </template>
                  </template>
                </paper-row>
              </div>
            </template>
          </iron-collapse>
          <template> </template
        ></template>
      </template>

      <katapult-dialog id="confirmationDialog" title="Confirm Mark Complete" maxWidth="250">
        <p>This action cannot be undone. Do you wish to mark this item completed?</p>
        <div style="display: flex; flex-direction: row; justify-content: flex-end;">
          <katapult-button small textColor="var(--text-color)" on-click="closeDialog">Cancel</katapult-button>
          <katapult-button small style="margin-left: 10px;" color="#007299" textColor="#f2f2f2" on-click="updateComplete"
            >Confirm</katapult-button
          >
        </div>
      </katapult-dialog>
    `;
  }
  static get properties() {
    return {
      // Booleans //
      // Row Open State Control
      rowToggleFlag: {
        type: Boolean,
        value: false
      },
      // Permissions and Modes
      moduleAdmin: {
        type: Boolean,
        value: false
      },
      moduleAdminMode: {
        type: Boolean,
        value: false
      },
      ableToEdit: {
        type: Boolean,
        value: false
      },
      companyAdmin: {
        type: Boolean,
        value: false
      },
      companyAdminMode: {
        type: Boolean,
        value: false
      },
      // Strings //
      // Date
      currentDate: {
        type: String
      },
      // Database Directory Paths
      allUserDataDirectory: {
        type: String,
        value: 'photoheight/home/training_widget/user_data'
      },
      moduleDataDirectory: {
        type: String,
        value: 'photoheight/home/training_widget/modules'
      },
      companyUserDataDirectory: {
        type: String,
        value: `photoheight/home/training_widget/user_data`
      },
      // Indexing for Confirmation Dialog
      moduleConfirmIndex: {
        type: String
      },
      itemConfirmIndex: {
        type: String
      },
      // Arrays //
      // Keys
      moduleKeys: {
        type: Array,
        value: []
      },
      itemKeys: {
        type: Array,
        value: []
      },
      // Dom-Repeat Arrays
      // Dom-repeat for module admin view (adding to/editing modules) and regular user view
      trainingData: {
        type: Array,
        value: []
      },
      // Array used for dropdown - updated to only show users not already in the database
      availableUserEmails: {
        type: Array,
        value: []
      },
      // Object used to hold data to add to Firebase - used when save button is clicked in company admin page
      updateUserDatabase: {
        type: Object,
        value: {}
      },
      // Object used to hold all the user data from company space (uid, creation date, etc)
      userData: {
        type: Object
      }
    };
  }

  static get observers() {
    return [
      'companyUsersUpdated(widgetUsers.*)',
      'myDataUpdated(myWidgetData.*)',
      'moduleDataUpdated(moduleData.*)',
      'companySpaceDataUpdated(companySpaceData.*)'
    ];
  }

  ready() {
    super.ready();
  }

  // START OF FIREBASE OBSERVER FUNCTIONS

  // Widget user data changes (new users, new assignments, new progress)
  companyUsersUpdated() {
    if (this.widgetUsers != null) {
      // Update the dom-repeat companyUsers array
      this.formatCompanyUsersArray();
      this.areAllUsersToggled();

      // Update a list of the users who aren't in the widget and are available to add
      this.generateListOfAvailableUsers();
      this.updateUserDatabase = structuredClone(this.widgetUsers);

      // Update the trainingData array using the new information from user_data
      this.formatTrainingData();
      //Update boolean that controls expand and collapse all
      this.areAllUserModulesToggled();
    }
  }

  // Active user data changes (completion, new assignment, new admin status)
  myDataUpdated() {
    if (this.myWidgetData != null) {
      // Check if admin status has changed
      this.moduleAdmin = this.myWidgetData?.admin || false;

      // Remove permissions if admin status changed
      if (!this.moduleAdmin) this.ableToEdit = this.moduleAdminMode = false;

      // Update the trainingData array using the new information from user_data
      this.formatTrainingData();
      //Update boolean that controls expand and collapse all
      this.areAllUserModulesToggled();
    }
  }

  // Module data changes (modules, items, completion requirements, links)
  moduleDataUpdated() {
    if (this.moduleData != null) {
      // Update the module keys, in case modules were added or deleted
      this.moduleKeys = Object.keys(this.moduleData);

      // Update trainingData while saving the current open statuses
      const openStatus = this.trainingData;
      this.trainingData = Object.values(this.moduleData);
      this.trainingData.forEach((module, moduleIndex) => {
        this.set(`trainingData.${moduleIndex}.adminopened`, openStatus[moduleIndex]?.adminopened || false);
        this.set(`trainingData.${moduleIndex}.useropened`, openStatus[moduleIndex]?.useropened || false);
      });

      // Update the trainingData array using the new information from modules
      this.formatTrainingData();
      //Update boolean that controls expand and collapse all
      this.areAllUserModulesToggled();
    }
  }

  // Defines this.companyAdmin and updates if admin status changes
  companySpaceDataUpdated() {
    if (this.companySpaceData != null) {
      // Is the active user in the admin page of the company space? If yes, company admin, if no, not admin
      this.companyAdmin = Object.keys(this.companySpaceData.admins).includes(this.user.uid) || false;

      // Get users within the relevant company
      this.userData = Object.values(this.companySpaceData.users);

      // Make a list of the users who aren't in the widget and are available to add
      this.generateListOfAvailableUsers();
    }
  }

  // END OF FIREBASE OBSERVER FUNCTIONS

  //Keep the date variable up to date
  setDate() {
    const date = new Date(DateTime.now());
    let month = '' + (date.getMonth() + 1);
    let day = '' + date.getDate();
    let hours = '' + date.getHours();
    let minutes = '' + date.getMinutes();
    let seconds = '' + date.getSeconds();
    this.currentDate = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${date.getFullYear()} ${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
  }

  // Toggles for the confirmation dialog (to mark items complete)
  openDialog(e) {
    this.shadowRoot.querySelector('#confirmationDialog').open();

    // The id of the element clicked has an ID of moduleIndex-itemIndex
    // Here, I grab these numbers to identify what item is being checked
    const indexes = e.currentTarget.id.split('-');
    this.moduleConfirmIndex = indexes[0];
    this.itemConfirmIndex = indexes[1];
  }

  closeDialog(e) {
    this.shadowRoot.querySelector('#confirmationDialog').close();
  }

  // ORGANIZE this.trainingData //
  formatTrainingData() {
    //Combine the user and general data array, and reinstate the module open state
    let trainingHolder = [];
    this.trainingData.forEach((module, moduleIndex) => {
      trainingHolder[moduleIndex] = this.updateDataArray(module.adminopened, module.useropened, module, moduleIndex);
    });
    this.trainingData = trainingHolder;
  }

  // This function formats this.trainingData, the array that is used for the user and module admin modes
  // This function runs inside of a this.traingingData.forEach(), meaning that the parameters are each individual module and their pieces
  updateDataArray(adminopenState, useropenState, module, moduleIndex) {
    // Set and define variables from globals etc that will be used often
    const userData = this.myWidgetData?.modules || {};
    const moduleKey = this.moduleKeys[moduleIndex];
    const userModuleData = userData[moduleKey];
    let [itemHolder, userModuleKeys] = [[], Object.keys(userData)];

    // Grab the item keys, as long as there are items (could evaluate false if a module was added but items weren't added under it yet)
    if (module.items != undefined) this.itemKeys[moduleIndex] = Object.keys(module.items);

    // If there are items, continue to format the item Array
    if (module.items != undefined) {
      // Loop items
      Object.values(module.items).forEach((item, itemIndex) => {
        // Grab the base data
        const currentItem = {
          description: item.description,
          link: item.link,
          assigned: false,
          markcomplete: item.markcomplete,
          title: item.title
        };

        // If there are assignments to the user, and this module is one of the assigned modules
        if (userModuleKeys.includes(moduleKey)) {
          const itemData = Object.values(userModuleData?.items);

          // Add the values relevant to the module being assigned
          currentItem.completed = itemData[itemIndex]?.completed;
          currentItem.date = itemData[itemIndex]?.date;
          currentItem.assigned = true;
        }

        itemHolder = itemHolder.concat([currentItem]);
      });
    }

    // Grab the base data
    const moduleObject = { items: itemHolder, useropened: useropenState, adminopened: adminopenState, moduletitle: module.moduletitle };

    const moduleAssigned = itemHolder[0]?.assigned;
    if (moduleAssigned) {
      // Add the values relevant to the module being assigned
      moduleObject.action = this.actionNeeded(itemHolder);
      moduleObject.progress = this.inProgress(itemHolder);
      moduleObject.dateCompleted = userModuleData?.date_completed;
    }
    return moduleObject;
  }

  setKeys() {
    this.moduleKeys = Object.keys(this.moduleData);
    Object.values(this.moduleData).forEach((module, moduleIndex) => {
      if (module.items != undefined) this.itemKeys[moduleIndex] = Object.keys(module.items);
    });
  }

  // ORGANIZE this.companyUsers //

  generateListOfAvailableUsers() {
    if (this.companyUsers == null) {
      this.availableUserEmails = this.userData.map((user) => user.email);
    } else {
      const currentUserUIDs = this.companyUsers.map((user) => user.uid);
      this.availableUserEmails = this.userData.filter((user) => !currentUserUIDs.includes(user.uid)).map((user) => user.email);
    }
  }

  formatCompanyUsersArray() {
    this.companyUsers = [];

    // The assumption is that the users directory is populated, but there's a chance it might not be, so protect against errors
    if (this.widgetUsers != undefined) {
      // Go through all the users in the company and see what modules they have assigned to them
      // Mark their assignment status, and populate the companyUsers array
      const userUIDs = Object.keys(this.widgetUsers);
      Object.values(this.widgetUsers).forEach((user, userIndex) => {
        const userModules = Object.keys(user.modules);
        let moduleStatusHolder = [];
        this.moduleKeys.forEach((id) => {
          if (userModules.includes(id)) {
            moduleStatusHolder.push({ title: this.moduleData[id].moduletitle, assigned: true });
          } else {
            moduleStatusHolder.push({ title: this.moduleData[id].moduletitle, assigned: false });
          }
        });
        this.companyUsers.push({
          email: user.email,
          uid: userUIDs[userIndex],
          name: this.widgetUsers[userUIDs[userIndex]].name ?? '',
          modules: moduleStatusHolder,
          opened: false
        });
      });

      // Make the enrolled users appear alphabetically
      this.orderCompanyUsersAlphabetically();
    }
  }

  orderCompanyUsersAlphabetically() {
    this.companyUsers.sort((a, b) => a.email.localeCompare(b.email));
  }

  // ROW TOGGLES //

  //Handle opening and closing module dropdowns
  toggleGroupRow(e) {
    // The id of the item clicked includes information of 1: What was clicked on, 2: What module index was clicked
    // Indexes[0] is the element clicked (the whole row or just the arrow), indexes[1] is the module index
    const indexes = e.currentTarget.id.split(' ');
    if (this.moduleAdminMode) {
      // Since the parent and child element both have the same on click value, this if conditional makes
      // Only the arrow work in editing mode, and only the parent click work outside of editing mode
      // If both worked at once, they could cancel each other out
      if ((indexes[0] === 'Arrow' && this.ableToEdit) || (indexes[0] === 'Title' && !this.ableToEdit)) {
        this.set(`trainingData.${indexes[1]}.adminopened`, !this.trainingData[indexes[1]]?.adminopened);
        //Update boolean that controls expand and collapse all
        this.areAllAdminModulesToggled();
      }
    } else {
      if (indexes[0] === 'Title') {
        this.set(`trainingData.${indexes[1]}.useropened`, !this.trainingData[indexes[1]]?.useropened);
        //Update boolean that controls expand and collapse all
        this.areAllUserModulesToggled();
      }
    }
  }

  //Toggle all modules open status
  toggleModules(e) {
    const mode = e.currentTarget.innerText;
    //True is expand, false is collapse
    this.rowToggleFlag = mode.includes('Expand') ? true : false;

    if (this.companyAdminMode) {
      this.companyUsers.forEach((user, userIndex) => {
        if (this.rowToggleFlag) {
          this.set(`companyUsers.${userIndex}.opened`, true);
        } else {
          this.set(`companyUsers.${userIndex}.opened`, false);
        }
      });
    } else if (this.moduleAdminMode) {
      this.trainingData.forEach((module, moduleIndex) => {
        if (this.rowToggleFlag) {
          this.set(`trainingData.${moduleIndex}.adminopened`, true);
        } else {
          this.set(`trainingData.${moduleIndex}.adminopened`, false);
        }
      });
    } else {
      this.trainingData.forEach((module, moduleIndex) => {
        if (this.rowToggleFlag) {
          this.set(`trainingData.${moduleIndex}.useropened`, true);
        } else {
          this.set(`trainingData.${moduleIndex}.useropened`, false);
        }
      });
    }
  }

  areAllAdminModulesToggled() {
    if (this.trainingData.every((module) => module.adminopened)) {
      this.rowToggleFlag = true;
    } else if (this.trainingData.every((module) => !module.adminopened)) {
      this.rowToggleFlag = false;
    }
  }

  areAllUserModulesToggled() {
    if (this.trainingData.every((module) => module.useropened)) {
      this.rowToggleFlag = true;
    } else if (this.trainingData.every((module) => !module.useropened)) {
      this.rowToggleFlag = false;
    }
  }

  // Toggles the open status of user data
  toggleUserRow(e) {
    if (!e.target.id.includes('Close')) {
      const email = e.currentTarget.id;
      this.companyUsers.forEach((user, index) => {
        if (email === user.email) this.set(`companyUsers.${index}.opened`, !user.opened);
      });
      this.areAllUsersToggled();
    }
  }

  areAllUsersToggled() {
    //Update boolean that controls expand and collapse all
    if (this.companyUsers) {
      if (this.companyUsers.every((user) => user.opened)) {
        this.rowToggleFlag = true;
      } else if (this.companyUsers.every((user) => !user.opened)) {
        this.rowToggleFlag = false;
      }
    }
  }

  // PROGRESS FUNCTIONS //

  //Handle clicks on check buttons
  updateComplete(e) {
    let moduleComplete = true;
    this.setKeys();
    this.setDate();
    this.closeDialog();
    this.trainingData.forEach((module, moduleIndex) => {
      if (moduleIndex == this.moduleConfirmIndex) {
        const moduleKey = this.moduleKeys[moduleIndex];
        Object.values(module?.items).forEach((item, itemIndex) => {
          if (itemIndex == this.itemConfirmIndex) {
            FirebaseWorker.ref(
              `/${this.companyUserDataDirectory}/${this.userGroup}/${this.user.uid}/modules/${moduleKey}/items/${this.itemKeys[moduleIndex][itemIndex]}`
            ).update({ completed: true, date: this.currentDate });
          } else if (item.completed === false) {
            moduleComplete = false;
          }
        });
      }
    });

    // If the module is completed, set the completion date for the whole module
    if (moduleComplete === true) {
      const moduleKey = this.moduleKeys[this.moduleConfirmIndex];
      FirebaseWorker.ref(`/${this.companyUserDataDirectory}/${this.userGroup}/${this.user.uid}/modules/${moduleKey}`).update({
        date_completed: this.currentDate
      });
    }
  }

  //Check if a module needs an action needed tag
  actionNeeded(itemData) {
    const checkableItems = itemData.filter((item) => item.markcomplete);
    if (checkableItems.length == 0) return false;
    return checkableItems.every((item) => !item.completed);
  }

  //Check if a module needs an in progress tag
  inProgress(itemData) {
    const checkableItems = itemData.filter((item) => item.markcomplete);
    if (checkableItems.length == 0) return false;
    return checkableItems.some((item) => item.completed) && !checkableItems.every((item) => item.completed);
  }

  // ADMIN CONTROLS //

  // This checks if you're in admin mode
  checkAdminMode(ifAdmin, ifInAdMode) {
    return ifAdmin && !ifInAdMode;
  }

  // This works to check both permissions and status. If you send in moduleAdminMode and companyAdminMode, you determine if you're in an admin mode
  // If you send in moduleAdmin and companyAdmin, you check if you are an admin of some sort
  amAdmin(modAdmin, compAdmin) {
    return modAdmin || compAdmin;
  }

  enableModuleAdminView() {
    this.moduleAdminMode = true;
    this.companyAdminMode = this.rowToggleFlag = false;
    this.updateUserDatabase = structuredClone(this.widgetUsers);
    this.shadowRoot.querySelector('#settingDropdown').close();
    this.areAllAdminModulesToggled();
  }

  enableCompanyAdminView() {
    this.companyAdminMode = true;
    this.moduleAdminMode = this.rowToggleFlag = false;
    this.shadowRoot.querySelector('#settingDropdown').close();
    this.areAllUsersToggled();
  }

  disableAdminView() {
    this.moduleAdminMode = false;
    this.companyAdminMode = this.rowToggleFlag = false;
    this.updateUserDatabase = structuredClone(this.widgetUsers);
    this.shadowRoot.querySelector('#settingDropdown').close();
    this.areAllUserModulesToggled();
  }

  // COMPANY ADMIN FUNCTIONS //

  addUser() {
    const user = this.shadowRoot.getElementById('user-selector').value;
    if (user != undefined) {
      let userHolder = this.companyUsers || [];
      let name = '';
      this.companyUsers = [];
      // Go through all the user data for the company, find the uid that matches the selected user
      this.userData.forEach((uid) => {
        if (user === uid.email) {
          if (uid.name != undefined) name = uid.name.last + ', ' + uid.name.first;
          this.updateUserDatabase[`${uid.uid}`] = { email: uid.email, modules: '', name: name };
          let moduleStatusHolder = [];
          this.moduleKeys.forEach((id) => {
            moduleStatusHolder.push({ title: this.moduleData[id].moduletitle, assigned: false });
          });
          userHolder.push({ email: uid.email, uid: uid.uid, modules: moduleStatusHolder, opened: false });
        }
      });
      this.companyUsers = userHolder;
      this.orderCompanyUsersAlphabetically();

      // Remove the email from the list of available users to-add
      this.availableUserEmails = this.availableUserEmails.filter((email) => email !== user);
      this.shadowRoot.getElementById('user-selector').value = '';
    }
  }

  deleteUser(e) {
    const indexes = e.currentTarget.id.split(' ');
    let userHolder = this.companyUsers;
    this.companyUsers = [];
    userHolder.forEach((user) => {
      if (user.email !== indexes[0]) {
        this.companyUsers.push(user);
      } else {
        this.availableUserEmails.push(user.email);
        this.updateUserDatabase[`${user.uid}`] = null;
      }
    });
  }

  // Update the database with the company user changes
  saveUserChanges() {
    FirebaseWorker.ref(`/${this.companyUserDataDirectory}/${this.userGroup}`).update(this.updateUserDatabase);
  }

  updateAssignment(e) {
    // Go through the users and update the dom-repeat array with the checked status, as well as grabbing the user uid
    let uid = '';
    this.companyUsers.forEach((user, userIndex) => {
      if (e.currentTarget.id === user.email) {
        uid = user.uid;
        user.modules.forEach((module, moduleIndex) => {
          if (module.title === e.currentTarget.innerText)
            this.set(`companyUsers.${userIndex}.modules.${moduleIndex}.assigned`, e.currentTarget.checked);
        });
      }
    });

    this.trainingData.forEach((module, moduleIndex) => {
      if (module.moduletitle === e.currentTarget.innerText) {
        // If the modules or uid dir of the update object are empty, initialize them as an object to allow property assignment
        if (!this.updateUserDatabase[`${uid}`]) this.updateUserDatabase[`${uid}`] = {};
        if (!this.updateUserDatabase[`${uid}`]['modules']) this.updateUserDatabase[`${uid}`]['modules'] = {};

        let updateBase = {};
        updateBase = this.updateUserDatabase[`${uid}`]['modules'][`${this.moduleKeys[moduleIndex]}`];
        // If the module is being assigned, update to add the module to the user's database
        if (e.currentTarget.checked) {
          // Check if the item was originally assigned and is now being reassigned, if so, clear the object
          if (updateBase === null) {
            updateBase = this.widgetUsers[`${uid}`][`modules`][`${this.moduleKeys[moduleIndex]}`];
          } else {
            // Add the module date
            updateBase = { date_completed: '', items: '' };

            // Add the module items
            // If the modules dir of the update object is empty, initialize it as an object to allow property assignment
            if (!updateBase['items']) updateBase['items'] = {};
            const itemKeys = Object.keys(Object.values(this.moduleData)[`${moduleIndex}`]['items']);
            itemKeys.forEach((key) => (updateBase['items'][`${key}`] = { completed: false, date: '' }));
          }
        }
        // If the module is being unassigned, update to remove the module from the user's database
        else {
          // Remove the module directory from under the user
          updateBase = null;
        }
        this.updateUserDatabase[`${uid}`]['modules'][`${this.moduleKeys[moduleIndex]}`] = updateBase;
      }
    });
  }

  // User Data Export Function
  exportUserData() {
    // Format the headers
    let columnHeaders = ['User'];
    this.trainingData.forEach((module) => {
      // When pushing this module title, format it to have a grey background
      columnHeaders.push(module.moduletitle);
      module.items.forEach((item) => columnHeaders.push(item.title));
      columnHeaders.push('');
    });

    // Format the rows, start with the first index being the header array
    let rows = [];
    rows.push(columnHeaders);
    const compUsers = this.companyUsers || [];

    // Go through all the users in the company
    compUsers.forEach((user) => {
      let userRow = [];
      // Get their name, go with their email if they don't have a name
      if (user.name === '') {
        userRow.push(user.email);
      } else {
        userRow.push(user.name);
      }

      // Get module data
      const UIDs = Object.keys(this.widgetUsers);
      Object.values(this.widgetUsers).forEach((databaseUser, userIndex) => {
        // Match up the current user with their module data
        if (user.uid === UIDs[userIndex]) {
          const moduleData = Object.values(this.moduleData);
          // If no modules are assigned, go through all the modules and items, and push empty for everything
          if (databaseUser.modules == undefined) {
            moduleData.forEach((module) => {
              userRow.push('');
              Object.values(module.items).forEach(() => userRow.push(''));
              userRow.push('');
            });

            // If modules are assigned, get progress data
          } else {
            const userModuleKeys = Object.keys(databaseUser.modules);
            moduleData.forEach((module, moduleIndex) => {
              // If the module is assigned populate dates, otherwise populate empty
              if (userModuleKeys.includes(this.moduleKeys[moduleIndex])) {
                const userModuleData = databaseUser.modules[this.moduleKeys[moduleIndex]];
                userRow.push(userModuleData.date_completed);
                Object.values(userModuleData.items).forEach((item) => userRow.push(item.date));
                userRow.push('');

                // Populate empty
              } else {
                userRow.push('');
                Object.values(module.items).forEach(() => userRow.push(''));
                userRow.push('');
              }
            });
          }
        }
      });
      // Push the user's row to the total rows array
      rows.push(userRow);
    });

    // Create workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Set Column Widths
    const widths = this.fixWidth(rows, rows[0]);
    const columnWidth = widths.map((width) => (width === 0 ? { wch: 10 } : { wch: width }));
    worksheet['!cols'] = columnWidth;
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    XLSX.writeFile(workbook, 'TrainingData.xlsx', { compression: true });
  }

  fixWidth(worksheet, headers) {
    // Get the header column lenths and set as the width
    let colLengths = headers.map((term) => term.length);

    let index = 0;
    // Go through each row
    for (const row of worksheet) {
      // Go through each cell, update the width if content is wider than header
      Object.values(row).forEach((element, eIndex) => {
        if (colLengths[eIndex] < element.toString().length) colLengths[eIndex] = element.toString().length;
      });
      index++;
    }
    return colLengths;
  }

  // MODULE ADMIN FUNCTIONS //

  // Save the text changes to the modules and items to the database
  updateData(e) {
    this.setKeys();
    const newText = e.currentTarget.value;
    const indexes = e.currentTarget.id.split(',');
    const moduleID = this.moduleKeys[indexes[0]];

    if (indexes.length === 2) {
      FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}`).update({ moduletitle: newText });
    } else {
      const itemID = this.itemKeys[indexes[0]][indexes[1]];
      if (indexes[2] === 'Link') {
        FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}/items/${itemID}`).update({ link: newText });
      } else if (indexes[2] === 'Title') {
        FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}/items/${itemID}`).update({ title: newText });
      } else if (indexes[2] === 'Description') {
        FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}/items/${itemID}`).update({ description: newText });
      }
    }
  }

  // Add a module to the training widget
  addModule() {
    const moduleID = firebase.database().ref().push().key;
    FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}`).update({ moduletitle: 'Module Title', items: '' });
  }

  // Add an item to a module
  addItem(e) {
    this.setKeys();
    const moduleID = this.moduleKeys[e.currentTarget.id];
    const itemID = firebase.database().ref().push().key;
    FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}/items/${itemID}`).update({
      title: 'Item Title',
      description: 'Description',
      markcomplete: false,
      link: ''
    });
  }

  // Delete a module
  deleteModule(e) {
    // Index is sent via ID to determine which module to delete
    this.setKeys();
    const index = parseInt(e.currentTarget.id.trim());
    const moduleID = this.moduleKeys[index];
    FirebaseWorker.database().ref(`/${this.moduleDataDirectory}/${moduleID}`).set(null);

    const companyNames = Object.keys(this.allUserData);
    Object.values(this.allUserData).forEach((company, cIndex) => {
      const userIds = Object.keys(company);
      Object.values(company).forEach((uid, userIndex) => {
        FirebaseWorker.database()
          .ref(`/${this.allUserDataDirectory}/${companyNames[cIndex]}/${userIds[userIndex]}/modules/${moduleID}`)
          .set(null);
      });
    });
  }

  // Delete a module item
  deleteItem(e) {
    // Index is sent via ID, first value is module index, then item index
    this.setKeys();
    const indexes = e.currentTarget.id.split('-');
    const moduleID = this.moduleKeys[indexes[0]];
    const itemID = this.itemKeys[indexes[0]][indexes[1]];
    FirebaseWorker.database().ref(`/${this.moduleDataDirectory}/${moduleID}/items/${itemID}`).set(null);

    const companyNames = Object.keys(this.allUserData);
    Object.values(this.allUserData).forEach((company, cIndex) => {
      const userIds = Object.keys(company);
      Object.values(company).forEach((uid, userIndex) => {
        FirebaseWorker.database()
          .ref(`/${this.allUserDataDirectory}/${companyNames[cIndex]}/${userIds[userIndex]}/modules/${moduleID}/items/${itemID}`)
          .set(null);
      });
    });
  }

  // Update the required status of an item
  makeRequired(e) {
    // Index is sent via ID, first value is module index, then item index
    this.setKeys();
    const indexes = e.currentTarget.id.split('.');
    const moduleID = this.moduleKeys[indexes[0]];
    const itemID = this.itemKeys[indexes[0]][indexes[1]];
    const requiredStatus = this.trainingData[indexes[0]].items[indexes[1]].markcomplete;

    FirebaseWorker.ref(`/${this.moduleDataDirectory}/${moduleID}/items/${itemID}`).update({ markcomplete: !requiredStatus });
  }

  // Change ability to add or delete modules
  toggleEditing() {
    this.ableToEdit = !this.ableToEdit;
  }
}
window.customElements.define('training-links-widget', TrainingWidget);
